// Theme constants for consistent styling across the application
export const THEME_CONSTANTS = {
  // Spacing values (multiples of theme.spacing)
  spacing: {
    xs: 1, // 8px
    sm: 2, // 16px
    md: 3, // 24px
    lg: 4, // 32px
    xl: 6, // 48px
    xxl: 8, // 64px
  },

  // Container padding responsive values
  containerPadding: {
    xs: 2, // 16px on mobile
    sm: 3, // 24px on tablet
    md: 4, // 32px on desktop
  },

  // Common border radius values
  borderRadius: {
    none: 0,
    small: 2,
    medium: 4,
    large: 4,
    xlarge: 8,
    round: "50%",
  },

  // Standard elevation levels
  elevation: {
    none: 0,
    card: 1,
    button: 2,
    modal: 8,
    tooltip: 16,
    drawer: 16,
    appBar: 4,
  },

  // Common z-index values
  zIndex: {
    hide: -1,
    auto: "auto",
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },

  // Icon sizes
  iconSize: {
    xs: 12,
    small: 16,
    medium: 24,
    large: 32,
    xlarge: 48,
    xxlarge: 64,
  },

  // Avatar sizes
  avatarSize: {
    xs: { width: 20, height: 20 },
    small: { width: 24, height: 24 },
    medium: { width: 32, height: 32 },
    large: { width: 40, height: 40 },
    xlarge: { width: 56, height: 56 },
    xxlarge: { width: 80, height: 80 },
  },

  // Button heights for consistency
  buttonHeight: {
    small: 32,
    medium: 40,
    large: 48,
    xlarge: 56,
  },

  // Button minimum widths for consistency
  buttonWidth: {
    small: 80,
    default: 120,
    medium: 160,
    large: 200,
  },

  // Form field heights
  fieldHeight: {
    small: 32,
    medium: 40,
    large: 48,
  },

  // Common transition durations
  transitions: {
    shortest: 100,
    short: 150,
    standard: 200,
    complex: 300,
    enteringScreen: 225,
    leavingScreen: 195,
  },

  // Consistent breakpoint usage
  breakpoints: {
    mobile: "xs",
    tablet: "sm",
    desktop: "md",
    large: "lg",
    xlarge: "xl",
  },

  // Typography scales
  typography: {
    fontSize: {
      xs: "0.75rem", // 12px
      sm: "0.875rem", // 14px
      base: "1rem", // 16px
      lg: "1.125rem", // 18px
      xl: "1.25rem", // 20px
      "2xl": "1.5rem", // 24px
      "3xl": "1.875rem", // 30px
      "4xl": "2.25rem", // 36px
      "5xl": "3rem", // 48px
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
      extraBold: 800,
    },
    lineHeight: {
      tight: 1.1,
      normal: 1.4,
      relaxed: 1.6,
      loose: 2,
    },
  },

  // Accessibility colors (WCAG 2.2 AA compliant)
  colors: {
    // Primary palette
    primary: {
      50: "#e3f2fd",
      100: "#bbdefb",
      200: "#90caf9",
      300: "#64b5f6",
      400: "#42a5f5",
      500: "#2196f3",
      600: "#1e88e5",
      700: "#1976d2",
      800: "#1565c0",
      900: "#0d47a1",
      main: "#1976d2",
      light: "#42a5f5",
      dark: "#1565c0",
      contrastText: "#ffffff",
    },

    // Semantic colors
    success: {
      50: "#e8f5e8",
      100: "#c8e6c8",
      200: "#a5d6a5",
      300: "#81c784",
      400: "#66bb6a",
      500: "#4caf50",
      600: "#43a047",
      700: "#388e3c",
      800: "#2e7d32",
      900: "#1b5e20",
      main: "#2e7d32",
      light: "#4caf50",
      dark: "#1b5e20",
      contrastText: "#ffffff",
    },

    warning: {
      50: "#fff8e1",
      100: "#ffecb3",
      200: "#ffe082",
      300: "#ffd54f",
      400: "#ffca28",
      500: "#ffc107",
      600: "#ffb300",
      700: "#ffa000",
      800: "#ff8f00",
      900: "#ff6f00",
      main: "#ed6c02",
      light: "#ffa726",
      dark: "#e65100",
      contrastText: "#000000",
    },

    error: {
      50: "#ffebee",
      100: "#ffcdd2",
      200: "#ef9a9a",
      300: "#e57373",
      400: "#ef5350",
      500: "#f44336",
      600: "#e53935",
      700: "#d32f2f",
      800: "#c62828",
      900: "#b71c1c",
      main: "#d32f2f",
      light: "#ef5350",
      dark: "#c62828",
      contrastText: "#ffffff",
    },

    info: {
      50: "#e1f5fe",
      100: "#b3e5fc",
      200: "#81d4fa",
      300: "#4fc3f7",
      400: "#29b6f6",
      500: "#03a9f4",
      600: "#039be5",
      700: "#0288d1",
      800: "#0277bd",
      900: "#01579b",
      main: "#0288d1",
      light: "#03a9f4",
      dark: "#01579b",
      contrastText: "#ffffff",
    },

    // Neutral colors
    grey: {
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#eeeeee",
      300: "#e0e0e0",
      400: "#bdbdbd",
      500: "#9e9e9e",
      600: "#757575",
      700: "#616161",
      800: "#424242",
      900: "#212121",
    },
  },

  // Common gradients
  gradients: {
    primary: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    success: "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
    warning: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
    error: "linear-gradient(135deg, #f87171 0%, #ef4444 100%)",
    info: "linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)",
    neutral: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
  },

  // Common box shadows (improved for accessibility)
  shadows: {
    none: "none",
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    base: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    inner: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)",
    card: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    cardHover:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    modal:
      "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    tooltip:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  },

  // Focus styles for accessibility
  focus: {
    ring: {
      width: 2,
      offset: 2,
      color: "#2196f3",
      style: "solid",
    },
    outline: {
      width: 2,
      offset: 2,
      color: "#2196f3",
      style: "solid",
    },
  },

  // Animation easing functions
  easing: {
    easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
    easeIn: "cubic-bezier(0.4, 0, 1, 1)",
    sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
  },

  // Layout constants
  layout: {
    headerHeight: 64,
    sidebarWidth: 240,
    sidebarWidthCollapsed: 64,
    contentMaxWidth: 1600,
    containerMaxWidth: 1800,
  },

  // Modal and dialog sizing for better UX
  modal: {
    // Maximum widths for different dialog types
    maxWidth: {
      xs: 320, // Very small dialogs (confirmations)
      sm: 400, // Small forms (login, simple settings)
      md: 600, // Standard forms (most common use case)
      lg: 800, // Large forms (detailed forms, settings)
      xl: 1000, // Very large dialogs (data tables, complex forms)
      full: "100vw", // Full screen when needed
    },
    // Responsive breakpoint overrides
    responsive: {
      xs: { maxWidth: "95vw", margin: 1 }, // Almost full width on mobile
      sm: { maxWidth: "90vw", margin: 2 }, // Give some breathing room on tablet
      md: { maxWidth: "80vw", margin: 3 }, // Desktop gets more margin
    },
    // Standard padding for dialog content
    padding: {
      xs: 2, // 16px
      sm: 3, // 24px
      md: 4, // 32px
    },
    // Dialog spacing
    spacing: {
      title: { pb: 2 }, // Space below dialog title
      content: { py: 1 }, // Vertical padding in content
      actions: { pt: 2, px: 3, pb: 2 }, // Action bar padding
    },
  },
} as const;

// Helper functions for consistent styling
export const getResponsivePadding = () => ({
  xs: THEME_CONSTANTS.containerPadding.xs,
  sm: THEME_CONSTANTS.containerPadding.sm,
  md: THEME_CONSTANTS.containerPadding.md,
});

export const getResponsiveSpacing = (
  size: keyof typeof THEME_CONSTANTS.spacing,
) => ({
  xs: THEME_CONSTANTS.spacing[size],
  sm: THEME_CONSTANTS.spacing[size],
  md: THEME_CONSTANTS.spacing[size],
});

// Common component styling mixins
export const commonStyles = {
  // Page container styling
  pageContainer: {
    py: getResponsivePadding(), // Only vertical padding
    px: 0, // No horizontal padding - content extends edge-to-edge
    width: "100%", // Full width - no max-width constraint
    minHeight: "calc(100vh - 64px)", // Account for header height
  },

  // Page header with consistent spacing and typography
  pageHeader: {
    mb: {
      xs: THEME_CONSTANTS.spacing.md,
      md: THEME_CONSTANTS.spacing.lg,
    },
    "& h1": {
      fontSize: {
        xs: THEME_CONSTANTS.typography.fontSize["2xl"],
        sm: THEME_CONSTANTS.typography.fontSize["3xl"],
        md: THEME_CONSTANTS.typography.fontSize["4xl"],
      },
      fontWeight: THEME_CONSTANTS.typography.fontWeight.semiBold,
      lineHeight: THEME_CONSTANTS.typography.lineHeight.tight,
      mb: THEME_CONSTANTS.spacing.xs,
      color: "text.primary",
    },
    "& p": {
      fontSize: {
        xs: THEME_CONSTANTS.typography.fontSize.sm,
        sm: THEME_CONSTANTS.typography.fontSize.base,
      },
      lineHeight: THEME_CONSTANTS.typography.lineHeight.relaxed,
      color: "text.secondary",
    },
  },

  // Standard card with enhanced accessibility
  // Matches MuiCard theme overrides for consistent border styling
  standardCard: {
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)",
    borderRadius: "4px",
    border: "1px solid #E2E8F0",
    transition: `all ${THEME_CONSTANTS.transitions.standard}ms ${THEME_CONSTANTS.easing.easeInOut}`,
    "&:hover": {
      boxShadow:
        "0 4px 12px rgba(99, 102, 241, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)",
      transform: "translateY(-1px)",
    },
    "&:focus-within": {
      outline: `${THEME_CONSTANTS.focus.outline.width}px ${THEME_CONSTANTS.focus.outline.style} ${THEME_CONSTANTS.focus.outline.color}`,
      outlineOffset: THEME_CONSTANTS.focus.outline.offset,
    },
  },

  // Interactive card (clickable)
  interactiveCard: {
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)",
    borderRadius: THEME_CONSTANTS.borderRadius.medium,
    border: "1px solid #E2E8F0",
    cursor: "pointer",
    transition: `all ${THEME_CONSTANTS.transitions.standard}ms ${THEME_CONSTANTS.easing.easeInOut}`,
    "&:hover": {
      boxShadow:
        "0 4px 12px rgba(99, 102, 241, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)",
      transform: "translateY(-1px)",
      borderColor: "primary.main",
    },
    "&:focus": {
      outline: `${THEME_CONSTANTS.focus.outline.width}px ${THEME_CONSTANTS.focus.outline.style} ${THEME_CONSTANTS.focus.outline.color}`,
      outlineOffset: THEME_CONSTANTS.focus.outline.offset,
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },

  // Standard button styling
  standardButton: {
    borderRadius: THEME_CONSTANTS.borderRadius.large,
    textTransform: "none" as const,
    fontWeight: THEME_CONSTANTS.typography.fontWeight.medium,
    transition: `all ${THEME_CONSTANTS.transitions.short}ms ${THEME_CONSTANTS.easing.easeInOut}`,
    minHeight: THEME_CONSTANTS.buttonHeight.medium,
    "&:focus-visible": {
      outline: `${THEME_CONSTANTS.focus.ring.width}px ${THEME_CONSTANTS.focus.ring.style} ${THEME_CONSTANTS.focus.ring.color}`,
      outlineOffset: THEME_CONSTANTS.focus.ring.offset,
    },
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },

  // Form field styling
  standardField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: THEME_CONSTANTS.borderRadius.large,
      minHeight: THEME_CONSTANTS.fieldHeight.medium,
      transition: `all ${THEME_CONSTANTS.transitions.short}ms ${THEME_CONSTANTS.easing.easeInOut}`,
      "&:focus-within": {
        outline: `${THEME_CONSTANTS.focus.ring.width}px ${THEME_CONSTANTS.focus.ring.style} ${THEME_CONSTANTS.focus.ring.color}`,
        outlineOffset: THEME_CONSTANTS.focus.ring.offset,
      },
    },
    "& .MuiInputLabel-root": {
      fontWeight: THEME_CONSTANTS.typography.fontWeight.medium,
    },
    "& .MuiFormHelperText-root": {
      fontSize: THEME_CONSTANTS.typography.fontSize.xs,
      marginTop: THEME_CONSTANTS.spacing.xs,
    },
  },

  // Dropdown/Select styling
  standardDropdown: {
    minHeight: THEME_CONSTANTS.fieldHeight.medium,
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      minHeight: THEME_CONSTANTS.fieldHeight.medium,
      paddingTop: 0,
      paddingBottom: 0,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: THEME_CONSTANTS.borderRadius.medium,
      "&:focus-within": {
        outline: `${THEME_CONSTANTS.focus.ring.width}px ${THEME_CONSTANTS.focus.ring.style} ${THEME_CONSTANTS.focus.ring.color}`,
        outlineOffset: THEME_CONSTANTS.focus.ring.offset,
      },
    },
  },

  // Table styling with proper alignment and enhanced spacing
  standardTable: {
    "& .MuiTableHead-root": {
      backgroundColor: "grey.50",
    },
    "& .MuiTableCell-root": {
      borderColor: "divider",
      py: { xs: 2, sm: 2.5, md: 3 }, // Enhanced row spacing: 16px, 20px, 24px
      px: { xs: 2, sm: 2.5, md: 3 }, // Enhanced column spacing
      fontSize: { xs: "0.8125rem", sm: "0.875rem" }, // Responsive font size
      lineHeight: 1.5,
    },
    "& .MuiTableCell-head": {
      fontWeight: THEME_CONSTANTS.typography.fontWeight.semiBold,
      color: "text.primary",
      backgroundColor: "grey.50",
      py: { xs: 2, sm: 2.5, md: 3 }, // Same enhanced spacing for headers
      fontSize: { xs: "0.8125rem", sm: "0.875rem" },
    },
    "& .MuiTableRow-root": {
      minHeight: { xs: 56, sm: 60, md: 64 }, // Taller rows for better readability
      transition: `background-color ${THEME_CONSTANTS.transitions.short}ms ${THEME_CONSTANTS.easing.easeInOut}`,
      "&:hover": {
        backgroundColor: "action.hover",
      },
      "&.clickable": {
        cursor: "pointer",
        "&:focus": {
          outline: `${THEME_CONSTANTS.focus.outline.width}px ${THEME_CONSTANTS.focus.outline.style} ${THEME_CONSTANTS.focus.outline.color}`,
          outlineOffset: -2,
        },
      },
    },
    // Alignment classes for table cells (Table Standards Guideline)
    "& .align-left": { textAlign: "left" },
    "& .align-center": { textAlign: "center" },
    "& .align-right": { textAlign: "right" },
    "& .center": { textAlign: "center" },

    // Data type specific alignment (Universal Standards)
    "& .numeric": {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace, system-ui',
    },
    "& .text": { textAlign: "left" },
    "& .alphanumeric-id": { textAlign: "left" }, // IDs are treated as text
    "& .numeric-id": {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace, system-ui',
    },
    "& .date": {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace, system-ui',
    },
    "& .duration": {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace, system-ui',
    },
    "& .currency": {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace, system-ui',
    },
    "& .name": { textAlign: "left" },
    "& .email": { textAlign: "left" },
    "& .status": { textAlign: "center" },
    "& .actions": {
      textAlign: "right",
      whiteSpace: "nowrap",
      width: "auto",
    },

    // Enhanced responsive behavior
    "@media (max-width: 768px)": {
      "& .MuiTableCell-root": {
        py: 1.5, // 12px on mobile
        px: 1.5,
        fontSize: "0.75rem",
      },
      "& .MuiTableRow-root": {
        minHeight: 48, // Smaller rows on mobile
      },
    },
  },

  // Loading state
  loadingState: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
    color: "text.secondary",
    gap: THEME_CONSTANTS.spacing.sm,
  },

  // Empty state
  emptyState: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
    color: "text.secondary",
    textAlign: "center" as const,
    p: THEME_CONSTANTS.spacing.lg,
    gap: THEME_CONSTANTS.spacing.sm,
  },

  // Flex layouts
  flexCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  flexBetween: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  flexStart: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },

  flexEnd: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },

  flexColumn: {
    display: "flex",
    flexDirection: "column" as const,
  },

  flexWrap: {
    display: "flex",
    flexWrap: "wrap" as const,
  },

  // Grid layouts
  gridAuto: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: THEME_CONSTANTS.spacing.md,
  },

  // Text utilities
  textTruncate: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },

  textTruncateMultiline: (lines: number = 2) => ({
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),

  // Accessibility utilities
  visuallyHidden: {
    position: "absolute" as const,
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap" as const,
    border: 0,
  },

  skipLink: {
    position: "absolute" as const,
    top: -40,
    left: 6,
    zIndex: THEME_CONSTANTS.zIndex.skipLink,
    backgroundColor: "primary.main",
    color: "primary.contrastText",
    padding: `${THEME_CONSTANTS.spacing.sm} ${THEME_CONSTANTS.spacing.md}`,
    borderRadius: THEME_CONSTANTS.borderRadius.medium,
    textDecoration: "none",
    fontWeight: THEME_CONSTANTS.typography.fontWeight.medium,
    "&:focus": {
      top: 6,
    },
  },

  // Focus styles
  focusVisible: {
    "&:focus-visible": {
      outline: `${THEME_CONSTANTS.focus.ring.width}px ${THEME_CONSTANTS.focus.ring.style} ${THEME_CONSTANTS.focus.ring.color}`,
      outlineOffset: THEME_CONSTANTS.focus.ring.offset,
    },
  },

  // Interactive states
  interactive: {
    cursor: "pointer",
    transition: `all ${THEME_CONSTANTS.transitions.short}ms ${THEME_CONSTANTS.easing.easeInOut}`,
    "&:hover": {
      opacity: 0.8,
    },
    "&:active": {
      transform: "scale(0.98)",
    },
  },

  // Status indicators
  statusIndicator: {
    display: "inline-flex",
    alignItems: "center",
    gap: THEME_CONSTANTS.spacing.xs,
    px: THEME_CONSTANTS.spacing.sm,
    py: THEME_CONSTANTS.spacing.xs,
    borderRadius: THEME_CONSTANTS.borderRadius.medium,
    fontSize: THEME_CONSTANTS.typography.fontSize.xs,
    fontWeight: THEME_CONSTANTS.typography.fontWeight.medium,
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
  },

  // Chip/Tag styling
  standardChip: {
    borderRadius: THEME_CONSTANTS.borderRadius.medium,
    fontWeight: THEME_CONSTANTS.typography.fontWeight.medium,
    fontSize: THEME_CONSTANTS.typography.fontSize.xs,
    height: 24,
    "& .MuiChip-label": {
      px: THEME_CONSTANTS.spacing.sm,
    },
  },

  // Avatar styling
  standardAvatar: {
    fontWeight: THEME_CONSTANTS.typography.fontWeight.medium,
    fontSize: THEME_CONSTANTS.typography.fontSize.xs,
  },

  // Icon button styling
  iconButton: {
    borderRadius: THEME_CONSTANTS.borderRadius.medium,
    transition: `all ${THEME_CONSTANTS.transitions.short}ms ${THEME_CONSTANTS.easing.easeInOut}`,
    "&:hover": {
      backgroundColor: "action.hover",
    },
    "&:focus-visible": {
      outline: `${THEME_CONSTANTS.focus.ring.width}px ${THEME_CONSTANTS.focus.ring.style} ${THEME_CONSTANTS.focus.ring.color}`,
      outlineOffset: THEME_CONSTANTS.focus.ring.offset,
    },
  },

  // Tooltip styling
  standardTooltip: {
    backgroundColor: "grey.800",
    color: "common.white",
    fontSize: THEME_CONSTANTS.typography.fontSize.xs,
    borderRadius: THEME_CONSTANTS.borderRadius.medium,
    padding: `${THEME_CONSTANTS.spacing.xs} ${THEME_CONSTANTS.spacing.sm}`,
    maxWidth: 300,
  },

  // Dialog and modal styling
  standardDialog: {
    "& .MuiDialog-paper": {
      borderRadius: THEME_CONSTANTS.borderRadius.large,
      boxShadow: THEME_CONSTANTS.shadows.modal,
    },
    "& .MuiDialogTitle-root": {
      borderBottom: "1px solid",
      borderColor: "divider",
      pb: THEME_CONSTANTS.spacing.md,
      mb: 0,
      fontWeight: THEME_CONSTANTS.typography.fontWeight.semiBold,
    },
    "& .MuiDialogContent-root": {
      py: THEME_CONSTANTS.spacing.md,
      px: THEME_CONSTANTS.spacing.md,
    },
    "& .MuiDialogActions-root": {
      borderTop: "1px solid",
      borderColor: "divider",
      px: THEME_CONSTANTS.spacing.md,
      py: THEME_CONSTANTS.spacing.md,
      gap: THEME_CONSTANTS.spacing.sm,
    },
  },

  // Form dialog specific styling
  formDialog: {
    "& .MuiDialog-paper": {
      borderRadius: THEME_CONSTANTS.borderRadius.large,
      boxShadow: THEME_CONSTANTS.shadows.modal,
      maxWidth: THEME_CONSTANTS.modal.maxWidth.md, // 600px for forms
    },
    "& .MuiDialogTitle-root": {
      borderBottom: "1px solid",
      borderColor: "divider",
      pb: THEME_CONSTANTS.spacing.md,
      mb: 0,
      fontWeight: THEME_CONSTANTS.typography.fontWeight.semiBold,
    },
    "& .MuiDialogContent-root": {
      py: THEME_CONSTANTS.spacing.md,
      px: THEME_CONSTANTS.spacing.md,
      minHeight: 200,
    },
    "& .MuiDialogActions-root": {
      borderTop: "1px solid",
      borderColor: "divider",
      px: THEME_CONSTANTS.spacing.md,
      py: THEME_CONSTANTS.spacing.md,
      gap: THEME_CONSTANTS.spacing.sm,
      justifyContent: "flex-end",
    },
    // Responsive behavior
    [THEME_CONSTANTS.breakpoints.mobile]: THEME_CONSTANTS.modal.responsive.xs,
    [THEME_CONSTANTS.breakpoints.tablet]: THEME_CONSTANTS.modal.responsive.sm,
  },

  // Confirmation dialog styling (smaller)
  confirmDialog: {
    "& .MuiDialog-paper": {
      borderRadius: THEME_CONSTANTS.borderRadius.large,
      boxShadow: THEME_CONSTANTS.shadows.modal,
      maxWidth: THEME_CONSTANTS.modal.maxWidth.sm, // 400px for confirmations
    },
    "& .MuiDialogContent-root": {
      py: THEME_CONSTANTS.spacing.md,
      px: THEME_CONSTANTS.spacing.md,
    },
    "& .MuiDialogActions-root": {
      px: THEME_CONSTANTS.spacing.md,
      py: THEME_CONSTANTS.spacing.md,
      gap: THEME_CONSTANTS.spacing.sm,
      justifyContent: "flex-end",
    },
  },
};

export default THEME_CONSTANTS;
